/**
 * Vehicle detail SEO metadata (Milestone 14).
 */
import { LISTING_PLACEHOLDER_IMAGE, PUBLIC_STATUS } from "../constants/automapListingConstants";
import { formatMileageKmLong, formatPriceCop, formatVehicleTitle } from "../utils/automapListing/listingFormatters";
import {
  selectVehicleMake,
  selectVehicleModel,
  selectVehicleTrim,
  selectVehicleYear,
} from "../utils/automapListing/canonicalVehicleTitle";
import { getListingPublicStatus } from "../utils/automapListing/listingVisibility";
import { buildAbsoluteUrl, getPublicVehicleDetailPath, resolveOgImageUrl } from "./canonicalUrls";
import { getVehicleNoindexDecision } from "./noindexRules";
import { buildBreadcrumbJsonLd, buildVehicleJsonLd, combineStructuredData } from "./structuredData";
import { AUTOMAPA_DEFAULT_OG_IMAGE, AUTOMAPA_PRODUCT_OG_TYPE, PUBLIC_SEARCH_PATH } from "./seoConstants";
import { buildMetaDescription, buildPageTitle, buildSeoBundle, robotsDirective, sanitizeSeoText } from "./seoUtils";

function hasVehicleIdentity(listing) {
  if (!listing) return false;
  return Boolean(
    selectVehicleYear(listing) ||
      selectVehicleMake(listing) ||
      selectVehicleModel(listing) ||
      selectVehicleTrim(listing)
  );
}

function buildVehicleSeoTitle(listing) {
  const identity = hasVehicleIdentity(listing) ? formatVehicleTitle(listing) : "";
  const city = sanitizeSeoText(listing?.city);

  if (!identity) {
    return buildPageTitle("Carro usado en Colombia");
  }

  const vehicleLabel = `${identity} usado`;
  if (city) {
    return buildPageTitle(`${vehicleLabel} en ${city}`);
  }

  return buildPageTitle(`${vehicleLabel} en Colombia`);
}

function buildVehicleSeoDescription(listing, { priceLabel, mileageLabel } = {}) {
  const identity = hasVehicleIdentity(listing) ? formatVehicleTitle(listing) : "";
  const city = sanitizeSeoText(listing?.city) || "Colombia";

  let description = identity
    ? `Revisa este ${identity} usado en ${city}. Compara precio, kilometraje, concesionario, señales de revisión y opciones similares en AutoMapa.`
    : `Revisa este carro usado en ${city}. Compara precio, kilometraje, concesionario, señales de revisión y opciones similares en AutoMapa.`;

  const extras = [];
  if (priceLabel && !/no disponible/i.test(priceLabel)) {
    extras.push(`Precio: ${priceLabel}.`);
  }
  if (mileageLabel && !/no disponible/i.test(mileageLabel)) {
    extras.push(`Kilometraje: ${mileageLabel}.`);
  }

  if (extras.length) {
    description = `${description} ${extras.join(" ")}`;
  }

  return buildMetaDescription(description);
}

export function buildVehicleDetailBreadcrumbs(listing, { homeHref = "/", searchHref = PUBLIC_SEARCH_PATH } = {}) {
  const items = [
    { label: "Inicio", href: homeHref },
    { label: "Carros usados", href: searchHref },
  ];

  if (listing?.city) {
    items.push({
      label: listing.city,
      href: `${searchHref}?city=${encodeURIComponent(listing.city)}`,
    });
  }

  const leaf = formatVehicleTitle(listing, { fallback: "Detalle" });
  items.push({ label: leaf });

  return items;
}

export function getVehicleDetailSeo(listing, dealer = null, options = {}) {
  const {
    pageStatus = "ready",
    homeHref = "/",
    searchHref = PUBLIC_SEARCH_PATH,
  } = options;

  const publicStatus = listing ? getListingPublicStatus(listing) : PUBLIC_STATUS.NOT_FOUND;
  const noindexDecision = getVehicleNoindexDecision({
    listing,
    dealer,
    publicStatus,
    status: pageStatus === "not_found" ? "not_found" : publicStatus,
  });

  const canonicalPath = listing ? getPublicVehicleDetailPath(listing) : null;
  const title = listing ? buildVehicleSeoTitle(listing) : buildPageTitle("Carro usado en Colombia");

  const priceLabel = listing ? formatPriceCop(listing.priceCop ?? listing.currentPriceCop) : null;
  const mileageLabel = listing ? formatMileageKmLong(listing.mileageKm) : null;

  const description = listing
    ? buildVehicleSeoDescription(listing, { priceLabel, mileageLabel })
    : buildMetaDescription(
        "Explora carros usados en Colombia con señales de precio, riesgo y checklist de compra en AutoMapa."
      );

  const image = listing?.primaryImageUrl && !String(listing.primaryImageUrl).includes("vehicle-placeholder")
    ? resolveOgImageUrl(listing.primaryImageUrl)
    : resolveOgImageUrl(AUTOMAPA_DEFAULT_OG_IMAGE);

  const bundle = buildSeoBundle({
    title,
    description,
    canonicalPath: canonicalPath || undefined,
    robots: robotsDirective(noindexDecision.shouldIndex, { follow: noindexDecision.follow }),
    image,
    ogType: AUTOMAPA_PRODUCT_OG_TYPE,
  });

  const breadcrumbs = listing
    ? buildVehicleDetailBreadcrumbs(listing, { homeHref, searchHref })
    : [];

  const structuredData = listing
    ? combineStructuredData(
        buildVehicleJsonLd(listing, dealer, { publicStatus }),
        buildBreadcrumbJsonLd(breadcrumbs)
      )
    : null;

  return {
    ...bundle,
    noindex: !noindexDecision.shouldIndex,
    noindexReason: noindexDecision.reason,
    breadcrumbs,
    structuredData,
    canonicalSlug: listing?.slug || null,
    displayTitle: listing ? formatVehicleTitle(listing) : null,
    fallbackImage: LISTING_PLACEHOLDER_IMAGE,
    shareUrl: canonicalPath ? buildAbsoluteUrl(canonicalPath) : undefined,
  };
}

export { buildVehicleSeoTitle, buildVehicleSeoDescription };
